import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  Card,
  CardBody,
  useColorModeValue,
  List,
  ListItem,
  ListIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tag,
  TagLabel,
  useToast,
  Badge,
  Button,
  Icon,
} from '@chakra-ui/react';
import { CheckCircleIcon, DownloadIcon, ViewIcon, LinkIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';
import PayerResponseDisplay from './PayerResponseDisplay';

const PayerResponseViewer = () => {
  const { caseId } = useParams();
  const { accessToken } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState([]);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [caseFiles, setCaseFiles] = useState([]);
  const [externalPatientId, setExternalPatientId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [loadingGuidelineId, setLoadingGuidelineId] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  // State for the new source document viewer modal
  const { isOpen: isSourceOpen, onOpen: onSourceOpen, onClose: onSourceClose } = useDisclosure();
  const [selectedSource, setSelectedSource] = useState(null);
  const [sourceContent, setSourceContent] = useState('');
  const [sourceLoading, setSourceLoading] = useState(false);

  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.700');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const codeBlockBg = useColorModeValue('gray.50', 'gray.800');

  useEffect(() => {
    const fetchResponses = async () => {
      if (!caseId || !accessToken) return;

      setLoading(true);
      setError(null);

      try {
        const response = await callApi(`/cases/${caseId}/payer-responses`, accessToken);
        const data = await response.json();

        if (response.ok && data.status === 'success') {
          setResponses(data.responses || []);
          if (data.responses && data.responses.length > 0) {
            setSelectedResponse(data.responses[0]);
          }
        } else {
          throw new Error(data.message || 'Failed to fetch payer responses.');
        }
      } catch (err) {
        console.error('Error fetching payer responses:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchResponses();
  }, [caseId, accessToken]);

  // Effect to fetch source content when a source is selected
  useEffect(() => {
    if (!selectedSource || !accessToken) return;

    const fetchSourceContent = async () => {
      setSourceLoading(true);
      setSourceContent('');
      try {
        const sourceId = selectedSource.id || selectedSource.source_id;
        if (!sourceId) throw new Error("Source has no valid ID.");

        const response = await callApi(`/content/sources/${sourceId}?disposition=inline`, accessToken);
        
        if (response.ok) {
          const text = await response.text();
          setSourceContent(text);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch source content');
        }
      } catch (err) {
        console.error('Error fetching source content:', err);
        toast({
          title: 'Failed to load source',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setSourceContent(`Error loading content: ${err.message}`);
      } finally {
        setSourceLoading(false);
      }
    };

    fetchSourceContent();
  }, [selectedSource, accessToken, toast]);


  const fetchCaseFiles = useCallback(async () => {
    if (!caseId || !accessToken) return;
    try {
        const response = await callApi(`/get_case_details/${caseId}`, accessToken);
        const data = await response.json();
        if (response.ok) {
            setCaseFiles(data.files || []);
            setExternalPatientId(data.external_patient_id || null);
        } else {
            console.error("Failed to fetch case files:", data.error);
        }
    } catch (err) {
        console.error("Error fetching case files:", err);
    }
  }, [caseId, accessToken]);

  useEffect(() => {
    fetchCaseFiles();
  }, [fetchCaseFiles]);

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleFileClick = (file) => {
    setSelectedFile(file);
    onOpen();
  };

  const handleSourceClick = (source) => {
    setSelectedSource(source);
    onSourceOpen();
  };

  const handleFileView = async (file) => {
    setFileLoading(true);
    try {
      const response = await callApi('/download_case_file', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          case_id: caseId,
          file_id: file.id,
          disposition: 'inline' // Tell the backend to serve the file for viewing
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');

        if (newTab) {
          newTab.focus();
        } else {
          alert('Please allow pop-ups for this website to view files.');
        }

        // Clean up the object URL after a short delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);

      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to open file');
      }
    } catch (error) {
      console.error('Error opening file:', error);
      toast({
        title: 'Failed to open file',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setFileLoading(false);
    }
  };

  const handleGuidelineView = async (source) => {
    const sourceId = source.id || source.source_id;
    if (!sourceId) {
        toast({
            title: 'Cannot view guideline',
            description: 'Source has no valid ID.',
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
        return;
    }

    setLoadingGuidelineId(sourceId);
    try {
      const response = await callApi(`/content/sources/${sourceId}/download`, accessToken);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const newTab = window.open(url, '_blank');

        if (newTab) {
          newTab.focus();
        } else {
          alert('Please allow pop-ups for this website to view files.');
        }

        // Clean up the object URL after a short delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 100);

      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to open guideline');
      }
    } catch (error) {
      console.error('Error opening guideline:', error);
      toast({
        title: 'Failed to open guideline',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingGuidelineId(null);
    }
  };

  if (loading) {
    return (
      <Box>
        <HeaderMenus />
        <Container maxW="container.xl" py={4} centerContent>
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text>Loading Case Data...</Text>
          </VStack>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <HeaderMenus />
        <Container maxW="container.xl" py={4}>
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertTitle>Error Loading Data</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={4}>
        <VStack spacing={4} align="stretch">
          <Box>
            <Heading as="h1" size="xl">
              Case Viewer
            </Heading>
            <Text fontSize="lg" color="gray.600">
              Viewing all appeals and documents for Case ID: {caseId}
            </Text>
          </Box>

          <Tabs variant="enclosed" colorScheme="blue">
            <TabList>
              <Tab>Appeal Letters</Tab>
              <Tab>Case Documents <Badge ml={2}>{caseFiles.length}</Badge></Tab>
              <Tab>Source Documents</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                  {/* Left Panel: List of Responses */}
                  <VStack align="stretch" spacing={4}>
                    <Heading size="md">Available Appeals</Heading>
                    <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" maxHeight="70vh" overflowY="auto">
                      <CardBody p={2}>
                        {responses.length > 0 ? (
                          <List spacing={2}>
                            {responses.map((response) => (
                              <ListItem
                                key={response.response_id}
                                p={3}
                                borderRadius="md"
                                cursor="pointer"
                                bg={selectedResponse?.response_id === response.response_id ? selectedBg : 'transparent'}
                                _hover={{ bg: selectedBg }}
                                onClick={() => setSelectedResponse(response)}
                              >
                                <HStack justify="space-between">
                                  <VStack align="start" spacing={0}>
                                    <Text fontWeight="bold">Claim: {response.claim_id || 'N/A'}</Text>
                                    <Text fontSize="sm" color="gray.500">
                                      Generated: {formatDate(response.created_at)}
                                    </Text>
                                  </VStack>
                                  {selectedResponse?.response_id === response.response_id && (
                                    <ListIcon as={CheckCircleIcon} color="green.500" />
                                  )}
                                </HStack>
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Text p={3} color="gray.500">No appeal letters have been generated for this case yet.</Text>
                        )}
                      </CardBody>
                    </Card>
                  </VStack>

                  {/* Right Panel: Display Selected Response */}
                  <Box gridColumn={{ base: 1, md: 'span 2' }}>
                    {selectedResponse ? (
                      <PayerResponseDisplay response={selectedResponse} />
                    ) : (
                      <Card bg={cardBg} borderColor={borderColor} centerContent p={10}>
                        <VStack>
                          <Heading size="md">Select an Appeal</Heading>
                          <Text color="gray.500">Choose an appeal from the list to view its details.</Text>
                        </VStack>
                      </Card>
                    )}
                  </Box>
                </SimpleGrid>
              </TabPanel>
              <TabPanel>
                {caseFiles.length > 0 ? (
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>File Name</Th>
                          <Th>Document Category</Th>
                          <Th>Size</Th>
                          <Th>Uploaded</Th>
                          <Th>Actions</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {caseFiles.map(file => (
                          <Tr key={file.id}>
                            <Td 
                              color="blue.600" 
                              textDecoration="underline"
                              cursor="pointer" 
                              onClick={() => handleFileClick(file)}
                              maxWidth="300px"
                              whiteSpace="normal"
                              wordBreak="break-word"
                            >
                              {file.file_name}
                            </Td>
                            <Td>
                              {file.document_category}
                            </Td>
                            <Td>{formatFileSize(file.file_size)}</Td>
                            <Td>{formatDate(file.upload_date)}</Td>
                            <Td>
                              <IconButton
                                icon={<ViewIcon />}
                                size="sm"
                                aria-label="View file"
                                title="View file in new tab"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFileView(file);
                                }}
                                isLoading={fileLoading}
                              />
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Text color="gray.500" fontStyle="italic">
                    No documents found for this case.
                  </Text>
                )}
              </TabPanel>
              <TabPanel>
                {!selectedResponse ? (
                  <Card bg={cardBg} borderColor={borderColor} centerContent p={10}>
                    <VStack>
                      <Heading size="md">No Appeal Selected</Heading>
                      <Text color="gray.500">Please select an appeal from the "Appeal Letters" tab to view its source documents.</Text>
                    </VStack>
                  </Card>
                ) : (
                  <VStack spacing={6} align="stretch">
                    <Heading size="lg">Source Documents for Claim: {selectedResponse.claim_id}</Heading>
                    
                    {/* Payer Guidelines */}
                    <Box>
                      <Heading size="md" mb={3}>Payer Guidelines</Heading>
                      {selectedResponse.sources_summary.payer_guideline_sources?.length > 0 ? (
                        <TableContainer>
                          <Table variant="simple" size="sm">
                            <Thead>
                              <Tr>
                                <Th>Guideline Title</Th>
                                <Th>Actions</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {selectedResponse.sources_summary.payer_guideline_sources.map(source => (
                                <Tr key={source.id || source.source_id}>
                                  <Td 
                                    color="blue.600" 
                                    textDecoration="underline"
                                    cursor="pointer" 
                                    onClick={() => handleSourceClick(source)}
                                    maxWidth="600px"
                                    whiteSpace="normal"
                                    wordBreak="break-word"
                                  >
                                    {source.title}
                                  </Td>
                                  <Td>
                                    <IconButton
                                      icon={<ViewIcon />}
                                      size="sm"
                                      aria-label="View guideline"
                                      title="View guideline in new tab"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleGuidelineView(source);
                                      }}
                                      isLoading={loadingGuidelineId === (source.id || source.source_id)}
                                    />
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Text color="gray.500">No payer guideline sources were used for this appeal.</Text>
                      )}
                    </Box>

                    {/* Appendix Sources */}
                    <Box>
                      <Heading size="md" mb={3}>Appendix Sources</Heading>
                      {selectedResponse.sources_summary.appendix_sources?.length > 0 ? (
                        <TableContainer>
                          <Table variant="simple" size="sm">
                            <Thead>
                              <Tr>
                                <Th>Source Title</Th>
                                <Th>Actions</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {selectedResponse.sources_summary.appendix_sources.map(source => (
                                <Tr key={source.id || source.source_id}>
                                  <Td 
                                    color="blue.600" 
                                    textDecoration="underline"
                                    cursor="pointer" 
                                    onClick={() => handleSourceClick(source)}
                                    maxWidth="600px"
                                    whiteSpace="normal"
                                    wordBreak="break-word"
                                  >
                                    {source.title}
                                  </Td>
                                  <Td>
                                    <IconButton
                                      icon={<ViewIcon />}
                                      size="sm"
                                      aria-label="View source"
                                      title="View source in new tab"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleGuidelineView(source); // Re-using the same handler
                                      }}
                                      isLoading={loadingGuidelineId === (source.id || source.source_id)}
                                    />
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Text color="gray.500">No appendix sources were used for this appeal.</Text>
                      )}
                    </Box>
                  </VStack>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Container>

      {/* Source Document Content Modal */}
      <Modal isOpen={isSourceOpen} onClose={onSourceClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>{selectedSource?.title || "Source Document"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box 
              p={4} 
              borderWidth="1px" 
              borderRadius="md" 
              bg={codeBlockBg}
              maxHeight="70vh" 
              overflowY="auto"
              whiteSpace="pre-wrap"
              fontFamily="monospace"
            >
              {sourceLoading ? <Spinner /> : (sourceContent || "No content available")}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onSourceClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* File Content Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>
            {selectedFile?.file_name}
            <Tag size="sm" colorScheme="blue" ml={2} mt={-1}>
              <TagLabel>{selectedFile?.file_type}</TagLabel>
            </Tag>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box 
              p={4} 
              borderWidth="1px" 
              borderRadius="md" 
              bg={codeBlockBg}
              maxHeight="70vh" 
              overflowY="auto"
              whiteSpace="pre-wrap"
              fontFamily="monospace"
            >
              {selectedFile?.file_text || "No content available"}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Text fontSize="sm" color="gray.500" mr="auto">
              {selectedFile ? formatFileSize(selectedFile.file_size) : ""} • 
              {selectedFile ? " Uploaded " + formatDate(selectedFile.upload_date) : ""}
            </Text>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PayerResponseViewer;